"use client";

import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import CountUp from "#/components/CountUp";
import { Card, CardContent } from "#/components/ui/card";
import { useGlobalStatsIntro } from "#/lib/globalStatsIntro";
import { globalStatsQueryOptions } from "#/lib/queries";

const GLOBAL_STATS_REFETCH_INTERVAL_MS = 20_000;

type GlobalStatKey = "mapsCleared" | "buildingsCaptured" | "unitsBuilt" | "unitsKilled";

const GLOBAL_STAT_ITEMS: Array<{ label: string; key: GlobalStatKey }> = [
  { label: "Maps cleared", key: "mapsCleared" },
  { label: "Buildings captured", key: "buildingsCaptured" },
  { label: "Units built", key: "unitsBuilt" },
  { label: "Units killed", key: "unitsKilled" },
];

export function GlobalStatsSection({ linkToStats = false }: { linkToStats?: boolean }) {
  const { playIntro, markIntroPlayed } = useGlobalStatsIntro();
  const { data: stats } = useSuspenseQuery({
    ...globalStatsQueryOptions(),
    refetchInterval: GLOBAL_STATS_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    return () => {
      markIntroPlayed();
    };
  }, [markIntroPlayed]);

  const card = (
    <Card className="w-full" data-nosnippet>
      <CardContent>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          {GLOBAL_STAT_ITEMS.map(({ label, key }) => (
            <div key={key} className="flex flex-col items-center gap-1 text-center">
              <p className="whitespace-nowrap text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {label}
              </p>
              <GlobalStatValue value={stats[key]} playIntro={playIntro} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  if (!linkToStats) return card;

  return (
    <Link
      to="/stats"
      preload={false}
      aria-label="View global statistics"
      className="block focus-visible:outline-none"
    >
      {card}
    </Link>
  );
}

function GlobalStatValue({ value, playIntro }: { value: number; playIntro: boolean }) {
  const previousValueRef = useRef<number | null>(playIntro ? null : value);
  const previousValue = previousValueRef.current;
  const from = previousValue ?? 0;
  const valueChanged = previousValue !== null && previousValue !== value;

  useEffect(() => {
    previousValueRef.current = value;
  }, [value]);

  if (!playIntro && !valueChanged) {
    return (
      <span className="text-3xl font-bold tabular-nums text-accent">
        {value.toLocaleString("en-US")}
      </span>
    );
  }

  return (
    <CountUp
      to={value}
      from={from}
      duration={1}
      separator=","
      placeholder={playIntro && previousValue === null ? "-" : undefined}
      className="text-3xl font-bold tabular-nums text-accent"
    />
  );
}
